import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient, ScoringConfigResponse } from '../services/api';
import { toast } from 'react-hot-toast';

interface ScoringConfigContextType {
    config: ScoringConfigResponse | null;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    // Convenience getters for thresholds
    lowMediumBoundary: number;
    mediumHighBoundary: number;
}

const defaultContext: ScoringConfigContextType = {
    config: null,
    loading: true,
    error: null,
    refresh: async () => {},
    lowMediumBoundary: 15,  // Default
    mediumHighBoundary: 40, // Default
};

export const ScoringConfigContext = createContext<ScoringConfigContextType>(defaultContext);

interface ScoringConfigProviderProps {
    children: React.ReactNode;
}

export const ScoringConfigProvider: React.FC<ScoringConfigProviderProps> = ({ children }) => {
    const [config, setConfig] = useState<ScoringConfigResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        try {
            setError(null);
            const data = await apiClient.getScoringConfig();
            setConfig(data);
        } catch (err: any) {
            console.error('Error fetching scoring config:', err);
            setError(err.message || 'Failed to load scoring configuration');
            toast.error('Could not load scoring configuration. Risk level thresholds may not reflect your organisation\'s settings.');
        }
    }, []);

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, [refresh]);

    // Convenience values with defaults
    const lowMediumBoundary = config?.riskLevelThresholds?.lowMediumBoundary ?? 15;
    const mediumHighBoundary = config?.riskLevelThresholds?.mediumHighBoundary ?? 40;

    return (
        <ScoringConfigContext.Provider
            value={{
                config,
                loading,
                error,
                refresh,
                lowMediumBoundary,
                mediumHighBoundary,
            }}
        >
            {children}
        </ScoringConfigContext.Provider>
    );
};

/**
 * Hook to access scoring configuration
 * Returns thresholds with defaults if config not loaded
 */
export const useScoringConfig = (): ScoringConfigContextType => {
    const context = useContext(ScoringConfigContext);
    if (!context) {
        throw new Error('useScoringConfig must be used within a ScoringConfigProvider');
    }
    return context;
};

/**
 * Hook to get risk level from score using org thresholds
 */
export const useRiskLevel = () => {
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();

    return useCallback((score: number): 'low' | 'medium' | 'high' => {
        if (score < lowMediumBoundary) return 'low';
        if (score < mediumHighBoundary) return 'medium';
        return 'high';
    }, [lowMediumBoundary, mediumHighBoundary]);
};
